import React from 'react';
import {useNavigate, useParams, useSearchParams, useLocation} from "react-router-dom";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import TabList from "@mui/joy/TabList";
import Tab, {tabClasses} from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";
import {OrderDetails} from "./orderTabs/OrderDetails";
import {Offers} from "./orderTabs/Offers";

export function Order() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const tabSlugs = ['general', 'offers'] as const;

    const getTabIndexFromLocation = (): number => {
        const hash = (location.hash || '').replace('#', '').trim();
        if (hash) {
            const idx = tabSlugs.indexOf(hash.toLowerCase() as typeof tabSlugs[number]);
            if (idx >= 0) return idx;
        }
        const tabParam = (searchParams.get('tab') || '').toLowerCase();
        const idxFromQuery = tabSlugs.indexOf(tabParam as typeof tabSlugs[number]);
        return idxFromQuery >= 0 ? idxFromQuery : 0;
    };

    const [currentTab, setCurrentTab] = React.useState<number>(0);

    React.useEffect(() => {
        setCurrentTab(getTabIndexFromLocation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash, location.search]);

    const handleTabChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const nextIndex = typeof value === 'number' ? value : 0;
        setCurrentTab(nextIndex);
        const slug = tabSlugs[nextIndex] || tabSlugs[0];
        navigate(`#${slug}`, { replace: false });
    };

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: {xs: 2, md: 6},
                    pt: {
                        xs: 'calc(12px + var(--Header-height))',
                        sm: 'calc(12px + var(--Header-height))',
                        md: 3,
                    },
                    pb: {xs: 2, sm: 2, md: 3},
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: {xs: 'start', sm: 'center'},
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Breadcrumbs
                        size="sm"
                        aria-label="breadcrumbs"
                        separator={<ChevronRightRoundedIcon/>}
                        sx={{pl: 0}}
                    >
                        <HomeRoundedIcon/>
                        <Link
                            underline="hover"
                            color="neutral"
                            sx={{fontSize: 12, fontWeight: 500}}
                            onClick={() => navigate('/app/orders')}
                        >
                            Заказы
                        </Link>
                        <Typography color="primary" sx={{fontWeight: 500, fontSize: 12}}>
                            {id}
                        </Typography>
                    </Breadcrumbs>
                </Box>
                <Typography level="h2" component="h1">
                    Заказ #{id}
                </Typography>
                <Tabs value={currentTab} onChange={handleTabChange} sx={{bgcolor: 'transparent'}}>
                    <TabList
                        tabFlex={1}
                        size="sm"
                        sx={{
                            pl: {xs: 0, md: 4},
                            justifyContent: 'left',
                            [`&& .${tabClasses.root}`]: {
                                fontWeight: '600',
                                flex: 'initial',
                                color: 'text.tertiary',
                                [`&.${tabClasses.selected}`]: {
                                    bgcolor: 'transparent',
                                    color: 'text.primary',
                                    '&::after': {
                                        height: '2px',
                                        bgcolor: 'primary.500',
                                    },
                                },
                            },
                        }}
                    >
                        <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={0}>
                            Общая информация
                        </Tab>
                        <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={1}>
                            Сделки
                        </Tab>
                    </TabList>
                    <TabPanel value={0}>
                        <OrderDetails />
                    </TabPanel>
                    <TabPanel value={1}>
                        <Offers />
                    </TabPanel>
                </Tabs>
            </Box>
        </Box>

    );
}
